import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Cake, Gift, ArrowRight, Loader2, Clock, PartyPopper } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FriendCountdownPage() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!shareToken) return;
    axios.get(`${API}/friends/countdown/${shareToken}`)
      .then(res => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [shareToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50 to-amber-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-pink-400" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-6" data-testid="friend-countdown-error">
        <div className="text-center">
          <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-700 mb-2">Link expired or not found</h1>
          <p className="text-sm text-slate-400 mb-4">This birthday countdown link may have been removed.</p>
          <Link to="/" className="text-sky-500 text-sm font-semibold hover:underline">
            Discover birthday deals
          </Link>
        </div>
      </div>
    );
  }

  const isToday = data.days_until === 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-amber-50 to-white" data-testid="friend-countdown-page">
      <div className="pt-12 pb-8 px-6 text-center">
        <div className={`w-20 h-20 mx-auto rounded-3xl flex items-center justify-center mb-6 ${
          isToday ? "bg-amber-200" : "bg-pink-100"
        }`}>
          {isToday ? <PartyPopper className="w-10 h-10 text-amber-600" /> : <Cake className="w-10 h-10 text-pink-500" />}
        </div>

        <h1 className="text-2xl font-black text-slate-800 mb-1" data-testid="friend-countdown-name">
          {isToday ? `Happy Birthday, ${data.name}!` : `${data.name}'s Birthday`}
        </h1>
        <p className="text-sm text-slate-400">{data.birthday_display}</p>

        <div className="mt-8 mb-6">
          {isToday ? (
            <div className="text-4xl font-black text-amber-500" data-testid="friend-countdown-number">TODAY!</div>
          ) : (
            <>
              <div className="text-6xl font-black text-pink-500" data-testid="friend-countdown-number">{data.days_until}</div>
              <p className="text-lg text-slate-500 font-medium mt-1">day{data.days_until !== 1 ? "s" : ""} to go</p>
            </>
          )}
        </div>
      </div>

      {data.freebie_count > 0 && (
        <div className="px-6 pb-12">
          <h2 className="text-lg font-bold text-slate-700 mb-4">{data.freebie_count} Birthday Deals Nearby</h2>
          <div className="space-y-2">
            {data.nearby_freebies.map((b, i) => (
              <div key={b.id || i} className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-slate-100 shadow-sm" data-testid={`friend-countdown-freebie-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <Gift className="w-5 h-5 text-emerald-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-700 truncate">{b.title}</p>
                  <p className="text-xs text-slate-400 truncate">{b.offer_summary || b.category}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="px-6 pb-12 text-center">
        <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-sky-500 text-white rounded-full text-sm font-bold hover:bg-sky-600 transition shadow-lg shadow-sky-200" data-testid="friend-countdown-cta">
          Discover your own birthday deals <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
